/**
 * AdminConversationRpcClient - AtomRpc integration for admin conversation endpoints
 *
 * This uses AtomRpc.Tag to create a special context tag that provides
 * proper integration with the atom runtime.
 *
 * Uses NDJSON serialization to support streaming responses.
 *
 * The ClerkTokenGetter layer reads the `__session` cookie directly (see
 * `readClerkSessionCookieToken`), falling back to `globalThis.__clerkSession`
 * when the cookie isn't present.
 *
 * @example
 * ```typescript
 * const createConversationAtom = AdminConversationRpcClient.runtime.fn(
 *   Effect.fnUntraced(function*(title?: string) {
 *     const client = yield* AdminConversationRpcClient;
 *     const result = yield* client("admin.conversation.create", { title });
 *     return result.id;
 *   })
 * );
 * ```
 *
 * @since 1.0.0
 */
import { AtomRpc } from "@effect-atom/atom-react";
import { FetchHttpClient } from "@effect/platform";
import { RpcSerialization } from "@effect/rpc";
import { AdminConversationRpcs } from "@fm/api/rpc";
import { Layer } from "effect";
import { makeRpcClientLayer, makeTokenGetterLayer } from "./shared.ts";

export class AdminConversationRpcClient extends AtomRpc.Tag<AdminConversationRpcClient>()(
  "AdminConversationRpcClient",
  {
    group: AdminConversationRpcs,
    protocol: makeRpcClientLayer(AdminConversationRpcs).pipe(
      Layer.provide(RpcSerialization.layerNdjson),
      Layer.provide(makeTokenGetterLayer()),
      Layer.provide(FetchHttpClient.layer),
    ),
  },
) {}
